<script>
import convertToHtml from '@rei/rich-text-renderer';
import { useDynamicContent } from '@rei/cs-preview-fetch';
import LandingFeaturedTiles from '@rei/landing-featured-tiles';
import LandingMiniBanner from '@rei/landing-mini-banner';
import { StoriesComponent } from '@rei/landing-story';
import { palettes } from '@rei/landing-palettes';
import FilmstripCarousel from '@rei/filmstrip-carousel';
import LongformComponent from '@rei/landing-longform-component';
import CouponPlacement from '../../common/components/coupon/CouponPlacement.vue';
import ComplexLead from '../../common/components/complex-lead/ComplexLead.vue';
import SecondaryHeading from '../../common/components/secondary-heading/SecondaryHeading.vue';
import OfferSet from './components/offer-set/OfferSet.vue';
import CategoryNav from '../../common/components/category-nav/CategoryNav.vue';
import MembershipPerks from '../../common/components/membership-perks/MembershipPerks.vue';
import AsyncProductRecommendations from '../../common/async-components/product-recommendations/AsyncProductRecommendations.vue';
import QuadGridCategories from './components/quad-grid/QuadGridCategories.vue';
import experimentation from '../../common/mixins/experimentation';

import analytics from '../../common/mixins/analytics';
import getRecs from '../../utils/recommendations';
import {
  configDestructuring,
  formatBannerContentStack,
  formatImageComponent,
  transformPromotionToContentBlock,
  transformPromotionToStory,
  formatMiniBannerComponent,
  transformLandingFeaturedTilesComponent,
  transformPromotionToLandingStories,
  formatOfferCards,
  transformImageAnchorsToBannerPosition,
  transformContentBlockSize,
} from '../../utils/formatContentStackData';

import logging from '../../common/mixins/logging';

export default {
  name: 'EventPage',
  components: {
    ComplexLead,
    LandingMiniBanner,
    StoriesComponent,
    SecondaryHeading,
    LandingFeaturedTiles,
    OfferSet,
    CategoryNav,
    CouponPlacement,
    MembershipPerks,
    FilmstripCarousel,
    AsyncProductRecommendations,
    QuadGridCategories,
    LongformComponent,
  },
  mixins: [analytics, logging, experimentation],
  props: {
    componentModels: { type: Object, default: () => ({}) },
    additionalData: { type: Object, default: () => ({}) },
    richRelevanceEnabled: { type: Boolean, default: true },
    adobeTargetOfferEnabled: { type: Boolean, default: true },
    testData: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const getPreviewUrl = () => `/home/rs/event/live-preview?entryId=${props.componentModels?.uid}`;
    const { content } = useDynamicContent(props, getPreviewUrl);
    if (content.value?.componentModels) {
      content.value = content.value.componentModels;
    }
    return {
      content,
    };
  },
  data() {
    return {
      recsPlacements: [],
      recsPlacementConfig: [
        { placementType: 'generic_page.asale_core_recs_ol_1' },
        { placementType: 'generic_page.asale_core_recs_ol_2' },
      ],
    };
  },
  computed: {
    renderingMap() {
      return {
        lead: this.lead,
        filmstrip: !!this.filmstrip,
        'secondary-lead': !!this.secondaryLead,
        catalog: !!this.catalog,
        'featured-stories': !!this.featuredStories,
        'membership-perks': !!this.membershipPerks,
        'offer-set': !!this.offerSet,
        'quad-grid-categories-top': !!this.quadGridCategoriesTop,
        'product-recommendations-top': (
          !!(this.richRelevanceEnabled
          && !!this.recsPlacements[1])
        ), // LP-3468
        'quad-grid-categories-bottom': !!this.quadGridCategoriesBottom,
        'additional-stories': !!this.additionalStories,
        'product-recommendations': !!(this.richRelevanceEnabled && !!this.recsPlacements[0]),
        'category-navigation': !!this.categoryNavigation,
        'featured-categories': !!this.featuredCategories,
        coupon: !!this.couponPlacement,
        'about-the-event': !!this.aboutTheEvent,
      };
    },
    uid() {
      const { uid } = this.content || {};
      return uid;
    },
    lead() {
      const { lead } = this.content || {};
      const { config } = lead || {};
      const contentAnchor = configDestructuring(lead, 'anchor');
      const contentAnchorMobile = configDestructuring(lead, 'anchorMobile');
      const textAlignment = configDestructuring(lead, 'copyAlignment');
      const textAlignmentDesktop = configDestructuring(lead, 'copyAlignmentDesktop');
      const secondaryButtonsOnly = configDestructuring(lead, 'secondaryButtonsOnly');
      const contentPosition = {
        xs: contentAnchorMobile,
        sm: contentAnchor,
      };
      const contentAlign = {
        xs: textAlignment,
        sm: textAlignmentDesktop,
      };
      const actionsOpt = {
        allSecondaryButtons: secondaryButtonsOnly,
      };
      const bannerDefaults = {
        bannerPosition: transformImageAnchorsToBannerPosition(lead?.imageAnchors),
        contentPosition,
      };
      const bannerComponentProps = (
        formatBannerContentStack({
          ...config,
        }, bannerDefaults)
      );
      const palette = configDestructuring(lead, 'palette');
      const size = transformContentBlockSize(config?.contentSize || 'xl-light');
      return (!!lead && {
        data: {
          media: formatImageComponent(lead?.image, {
            renderedSizes: {
              mobile: '100vw',
              desktop: '100vw',
            },
          }),
          palette,
          content: (transformPromotionToContentBlock(
            lead,
            {
              ...config,
              palette,
              contentAlign,
            },
            {
              level: 'h1',
              size,
            },
            actionsOpt
          )),
          bannerComponentProps,
          videoFocalPoint: null,
        },
      }) || null;
    },
    filmstrip() {
      const { filmStrip } = this.content || {};
      const renderComponent = !!filmStrip;
      const slides = filmStrip?.slides?.map((slide) => ({
        cta: {
          ...slide?.cta,
          target: slide?.cta?.href,
        },
        media: formatImageComponent(slide.media, {
          renderedSizes: {
            mobile: '33vw',
            desktop: '25vw',
          },
        }),
      }));

      return (renderComponent && {
        ...filmStrip,
        slides,
      }) || null;
    },
    quadGridCategoriesTop() {
      const { quadGridCategoriesData } = this.content || {};
      const renderComponent = !!quadGridCategoriesData?.top;
      return (renderComponent && {
        ...quadGridCategoriesData?.top,
      }) || null;
    },
    quadGridCategoriesBottom() {
      const { quadGridCategoriesData } = this.content || {};
      const renderComponent = !!quadGridCategoriesData?.bottom;
      return (renderComponent && {
        ...quadGridCategoriesData?.bottom,
      }) || null;
    },
    secondaryLead() {
      const { secondaryLead } = this.content || {};
      const palette = configDestructuring(secondaryLead, 'palette');

      const config = {
        padding: 'tight',
        palette: {
          ...palette,
        },
        background: palette?.surface,
      };
      const contentBlockHeadingOpts = {
        size: 'md-standard',
      };
      const promotion = {
        lockup: {},
        image: {},
        copy: {
          heading: secondaryLead?.heading,
          subheading: secondaryLead?.subheading,
          description: secondaryLead?.description,
        },
        links: secondaryLead?.links,
        config,
      };

      const secondaryLeadMap = formatMiniBannerComponent(
        promotion,
        config,
        contentBlockHeadingOpts,
      );
      const renderComponent = !!secondaryLead;
      return (renderComponent && secondaryLeadMap) || null;
    },
    additionalStories() {
      const { additionalStories } = this.content || {};
      const id = additionalStories?.id;
      const landingStoryConfig = {
        grid: 'double',
        aspectRatio: '5/4',
        palette: 'Sale with Secondary Background',
      };
      const headingOpts = {
        size: 'sm-standard',
        level: 'h2',
      };
      const headingStoriesOpts = {
        size: 'lg-standard',
        level: 'h3',
      };
      const actionsOpts = {
        useCustomAnalyticId: true,
        useCustomText: true,
        allSecondaryButtons: true,
      };
      const config = {
        theme: 'sale',
      };
      const mapPromotions = additionalStories?.promotions?.map((promotion) => ({
        ...promotion,
        actionsOptions: actionsOpts,
      }));

      const stories = (!!mapPromotions?.length && transformPromotionToLandingStories(
        mapPromotions,
        landingStoryConfig,
        null,
        headingOpts,
        config,
        headingStoriesOpts,
        null,
      )) || [];

      const renderComponent = !!stories?.stories?.length;
      return (
        (renderComponent && {
          id: id || 'complex-featured-stories',
          stories,
        }) || null
      );
    },
    catalog() {
      const { catalog } = this.content || {};
      const { promotion } = catalog || {};
      const palette = configDestructuring(catalog, 'palette');
      // Ratio property is part of a-sale hotfix
      const mediaOptions = {
        renderedSizes: {
          mobile: '(min-width: 768px) 45vw, 100vw',
          desktop: '568px',
        },
        ratio: '4/3',
      };
      return (!!catalog && {
        stories: [
          transformPromotionToStory(
            promotion,
            {
              palette,
            },
            {
              level: 'h2', size: 'sm-standard',
            },
            null,
            promotion?.image,
            mediaOptions,
            'light'
          ),
        ],
        aspectRatio: '4/3',
        grid: 'single',
      }) || null;
    },
    featuredStories() {
      const { featuredStories = {} } = this.content || {};
      const { promotionSections = [] } = featuredStories || {};
      const mapPromotions = promotionSections.map((promotionSection) => ({
        ...promotionSection.promotion,
        config: {
          ...promotionSection?.config,
          theme: configDestructuring(promotionSection, 'theme'),
        },
        actionsOptions: {
          allSecondaryButtons: configDestructuring(featuredStories, 'secondaryButtonsOnly'),
        },
      }));
      const headingOptions = { level: 'h2', size: 'sm-standard' };
      const landingStoryOptions = {
        grid: 'double',
        aspectRatio: '5/4',
      };
      // Ratio property is part of a-sale hotfix
      const mediaOptions = {
        renderedSizes: {
          mobile: '100vw',
          desktop: '568px',
        },
        ratio: landingStoryOptions.aspectRatio,
      };
      const formattedStories = transformPromotionToLandingStories(
        mapPromotions,
        landingStoryOptions,
        null,
        null,
        null,
        headingOptions,
        mediaOptions,
      );
      const renderComponent = !!mapPromotions?.length;
      return (renderComponent && { ...formattedStories }) || null;
    },
    membershipPerks() {
      const { membershipPerks } = this.content || {};
      const { heading, cta, perks } = membershipPerks || {};
      const mapPerks = perks?.map(({
        heading: perksHeading = '',
        body = '',
        image = {},
        cta: perksCta = {},
      } = {}) => ({
        heading: perksHeading,
        body,
        media: formatImageComponent(image),
        cta: perksCta,
      }));

      const renderComponent = !!membershipPerks;
      return (renderComponent && {
        heading,
        cta,
        perks: mapPerks,
      }) || null;
    },
    offerSet() {
      const { offerSet } = this.content || {};
      const cards = formatOfferCards(offerSet);
      const renderComponent = !!offerSet;
      return (renderComponent && { cards }) || null;
    },
    displayCategoryNavigation() {
      const { categoryNavigation } = this.content || {};
      return categoryNavigation?.display;
    },
    categoryNavigation() {
      if (!this.displayCategoryNavigation) return null;
      const { 'category-navigation': categoryNavigationList } = this.additionalData || {};
      const { children: category, text: categoryText } = categoryNavigationList || {};
      const mapped = (category && Object.values(category).map((subcategory) => {
        const { children, text, href } = subcategory;
        const mappedChildren = Object.values(children)
          .map((child) => ({ text: child.text, href: child.href }));
        return {
          children: mappedChildren,
          text,
          href,
        };
      })) || [];
      const currentPalette = palettes({ surface: 'primary' });
      const cssVars = {
        ...currentPalette.cssSurfacePaletteVars(),
      };
      const props = {
        linkCategories: mapped,
        categoryText,
        cssVars,
      };
      return mapped.length ? props : null;
    },
    featuredCategories() {
      const { featuredTiles } = this.content || {};
      const { heading } = featuredTiles || {};
      const config = { isMediaFullWidth: false };
      const props = transformLandingFeaturedTilesComponent(featuredTiles, config);
      const renderComponent = !!props?.featuredTiles?.length;
      return (renderComponent && {
        heading,
        props,
      }) || null;
    },
    couponPlacement() {
      const { couponPlacement } = this.content || {};
      const palette = configDestructuring(couponPlacement, 'palette');
      const {
        couponInformation: {
          startDate,
          endDate,
          couponCode,
        } = {},
        categories = {},
      } = couponPlacement || {};

      const image = couponPlacement?.content?.image;

      return couponPlacement && {
        palette,
        coupon: {
          content: transformPromotionToContentBlock({
            ...couponPlacement,
            ...couponPlacement.content,
            copy: {
              ...couponPlacement.content,
            },
          }, {
            palette,
          }, {
            size: 'md-standard',
          }),
          media: formatImageComponent(image, {}),
          mergedMediaAndActions: {
            text: {
              heading: {
                text: categories.heading,
              },
            },
            mediaAndActions: {
              categories: categories.categories.reduce((acc, item, index) => {
                acc[`category-${index + 1}`] = {
                  text: item?.link?.title,
                  target: item?.link?.href,
                  media: {
                    alt: item?.image?.altText,
                    src: item?.image?.src,
                  },
                };

                return acc;
              }, {}),
            },
          },
          children: {
            'resource-1': {
              config: {
                'start-date': startDate,
                'end-date': endDate,
                'coupon-code': couponCode,
              },
            },
          },
        },
      };
    },
    aboutTheEvent() {
      const { aboutTheEvent } = this.content || {};
      const { body } = aboutTheEvent || {};
      const mapBody = body?.map((doc) => {
        const markup = convertToHtml(doc);
        return {
          markup,
        };
      });
      return (!!aboutTheEvent && { body: mapBody }) || null;
    },
    reactiveComponentModels() {
      return {
        ...this.componentModels,
        expandDisruptorModalTest: {},
      };
    },
  },
  async mounted() {
    const promises = [];
    if (this.richRelevanceEnabled) {
      promises.push(this.initRecs());
    }
    if (!this.isLivePreview) {
      await this.initExperimentation({
        clientSideEnabled: this.adobeTargetOfferEnabled,
      });
    }

    if (promises.length) {
      // eslint-disable-next-line
      await Promise.all(promises).catch(() => {
        console.log('promise all failed');
      });
    }

    this.isMounted = true;
  },
  methods: {
    async initRecs() {
      return getRecs(this.recsPlacementConfig).then((data) => {
        this.recsPlacements = data;
        return Promise.resolve(data);
      });
    },
  },
};
</script>

<template>
  <div class="event-page event-page__stack">
    <!-- Complex Lead -->
    <section
      v-if="renderingMap.lead"
      :data-location-name="analyticsMap['lead']"
      data-ui="lead"
      class="event__lead"
      :class="lead.className"
    >
      <complex-lead v-bind="lead.data" />
    </section>

    <!-- filmstrip -->
    <section
      v-if="renderingMap.filmstrip"
      id="filmstrip"
      data-ui="filmstrip"
      :data-location-name="analyticsMap.filmstrip"
      class="event-page__filmstrip event-page__container"
    >
      <filmstrip-carousel v-bind="filmstrip" />
    </section>

    <!-- Secondary lead -->
    <landing-mini-banner
      v-if="renderingMap['secondary-lead']"
      v-bind="secondaryLead"
      ref="secondary-lead"
      :data-location-name="analyticsMap['secondary-lead']"
      data-ui="secondary-lead"
    />

    <!-- catalog -->
    <section
      v-if="renderingMap.catalog"
      :data-location-name="analyticsMap.catalog"
      data-ui="catalog"
      class="event-page__container event-page__catalog"
    >
      <stories-component v-bind="catalog" />
    </section>

    <!-- featured-stories -->
    <section
      v-if="renderingMap['featured-stories']"
      :data-location-name="analyticsMap['featured-stories']"
      data-ui="featured-stories"
      class="event-page__container event-page__stack event-page__featured-stories"
    >
      <stories-component v-bind="featuredStories" />
    </section>

    <!-- Offer Set -->
    <section
      v-if="renderingMap['offer-set']"
      :data-location-name="analyticsMap['offer-set']"
      data-ui="offer-set"
      class="event-page__container"
    >
      <offer-set v-bind="offerSet" />
    </section>

    <!-- Quad Grid Categories Top (Winter Sale B, C) -->
    <quad-grid-categories
      v-if="renderingMap['quad-grid-categories-top']"
      v-bind="quadGridCategoriesTop"
      :data-location-name="analyticsMap['quad-grid-categories-top']"
      data-ui="quad-grid-categories-top"
      class="event-page__quad-grid-categories event-page__container"
    />

    <!-- Product Recommendations (Winter Sale C) -->
    <section
      v-if="renderingMap['product-recommendations-top']"
      :data-location-name="analyticsMap['product-recommendations-top']"
      data-ui="product-recommendations-top"
      class="event-page__recommendations event-page__container"
    >
      <async-product-recommendations
        :placement="recsPlacements[1]"
        :metrics="metrics"
      />
    </section>

    <!-- Quad Grid Categories Bottom (Winter Sale B, C) -->
    <quad-grid-categories
      v-if="renderingMap['quad-grid-categories-bottom']"
      v-bind="quadGridCategoriesBottom"
      :data-location-name="analyticsMap['quad-grid-categories-bottom']"
      data-ui="quad-grid-categories-bottom"
      class="event-page__quad-grid-categories event-page__container"
    />

    <!-- Additional Stories -->
    <section
      v-if="renderingMap['additional-stories']"
      :data-location-name="analyticsMap['additional-stories']"
      data-ui="additional-stories"
      class="event-page__container event-page__additional-stories"
    >
      <stories-component
        v-bind="additionalStories.stories"
      />
    </section>

    <!-- Membership perks -->
    <section
      v-if="renderingMap['membership-perks']"
      :data-location-name="analyticsMap['membership-perks']"
      data-ui="membership-perks"
    >
      <membership-perks v-bind="membershipPerks" />
    </section>

    <!-- Product recommendations bottom -->
    <section
      v-if="renderingMap['product-recommendations']"
      :data-location-name="analyticsMap['product-recommendations']"
      data-ui="product-recommendations"
      class="event-page__recommendations event-page__container"
    >
      <async-product-recommendations
        :placement="recsPlacements[0]"
        :metrics="metrics"
      />
    </section>

    <!-- category navigation -->
    <section
      v-if="renderingMap['category-navigation']"
      :data-location-name="analyticsMap['category-navigation']"
      data-ui="category-nav"
      :style="categoryNavigation?.cssVars"
      class="event-page__category-navigation"
    >
      <div class="event-page__container event-page__category-navigation--inner">
        <category-nav v-bind="categoryNavigation" />
      </div>
    </section>

    <!-- featured tiles -->
    <section
      v-if="renderingMap['featured-categories']"
      :data-location-name="analyticsMap['featured-categories']"
      data-ui="featured-categories"
      class="event-page__container event-page__categories"
    >
      <secondary-heading :text="featuredCategories.heading" />
      <landing-featured-tiles v-bind="featuredCategories.props" />
    </section>

    <!-- Coupon -->
    <coupon-placement
      v-if="renderingMap.coupon"
      v-bind="couponPlacement"
      :data-location-name="analyticsMap.coupon"
      data-ui="coupon"
    />

    <!-- About the event -->
    <section
      v-if="renderingMap['about-the-event']"
      :data-location-name="analyticsMap['about-the-event']"
      data-ui="about-the-event"
      class="event-page__container event-page__about"
    >
      <hr>
      <div class="event-page__about-container">
        <longform-component
          v-for="(body, index) in aboutTheEvent?.body"
          :key="`subSection-${index + 1}`"
          class="event-page__about-content"
          :section="body"
        />
      </div>
    </section>
  </div>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';
@import '@rei/cdr-component-variables/dist/scss/index.scss';
.event-page {

  &__container {
    @include cdr-container;
    margin-top: $cdr-space-three-x;
  }

  &__catalog {
    .story__container--content {
      order: -1;
    }
  }

  &__category-navigation {
    padding: $cdr-space-three-x 0 calc($cdr-space-three-x + $cdr-space-three-quarter-x);
    background-color: var(--palette-background-color);

    &--inner {
      margin-top: 0;
    }
  }

  &__about-container {
    @include cdr-grid-base-mixin;
    row-gap: $cdr-space-half-x;
    grid-template-columns: 1fr;
    @include cdr-sm-mq-up {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__about-content {
    h3 {
      @include cdr-text-heading-serif-600;
      margin: 0 0 $cdr-space-three-quarter-x;
    }
  }

  &__recommendations {
    overflow: hidden;
  }

  &__filmstrip {
    margin-top: $cdr-space-two-x;
    margin-bottom: $cdr-space-two-x;
  }

  // Hot fix for a-sale.
  &__featured-stories {
    .story__image {
      border-radius: 0.8rem;
    }
  }

  // LP-3399
  &__additional-stories {
    .ls-content-block__subheading {
      color: $cdr-color-text-sale;
    }
  }
}
</style>
